import { format } from 'date-fns';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  Result,
  getDevice,
  getDeviceTimeline,
  sendUpgradeToLatestCommand,
} from '../../api/api';
import {
  IDevice,
  IDeviceTimelineItem,
  IDeviceTimelineResponse,
} from '../../api/types';
import { Button } from '../Common/Button';
import { Section } from '../Common/Section';
import { DeviceBasicInformation } from '../DeviceBasicInformation/DeviceBasicInformation';
import { DeviceCommandStatus } from './DeviceCommandStatus';
import { SendSetFirmware } from './SendSpecificFirmware';
import {
  getDeviceTimelineItemKey,
  getOldestPendingCommandTimestamp,
  mergeDeviceTimeline,
} from './deviceEvents';

type DeviceParams = {
  productCode: string;
  deviceId: string;
};

type EventLoadMode = 'initial' | 'newer' | 'older';

const EVENT_POLL_INTERVAL_MS = 15_000;
const EVENT_OVERLAP_MS = 1_000;
const EVENT_HISTORY_WINDOW_MS = 30 * 24 * 60 * 60 * 1_000;

export const Device = () => {
  const { deviceId } = useParams<DeviceParams>();

  const [deviceResult, setDeviceResult] = useState<Result<IDevice>>({
    status: '',
  });
  const [deviceTimeline, setDeviceTimeline] = useState<
    Result<IDeviceTimelineResponse>
  >({
    status: '',
  });
  const [deviceRefreshError, setDeviceRefreshError] = useState<
    string | undefined
  >();
  const [eventsRefreshing, setEventsRefreshing] = useState(false);
  const [loadingOlderEvents, setLoadingOlderEvents] = useState(false);
  const [olderTimelineWindowEmpty, setOlderTimelineWindowEmpty] =
    useState(false);
  const [eventsUpdatedAt, setEventsUpdatedAt] = useState<
    Date | undefined
  >();
  const [eventRefreshError, setEventRefreshError] = useState<
    string | undefined
  >();

  const timelineRef = useRef<IDeviceTimelineItem[]>([]);
  const newestTimelineItemRef = useRef<
    IDeviceTimelineItem | undefined
  >(undefined);
  const oldestHistoryBoundaryRef = useRef<string | undefined>(
    undefined,
  );
  const eventRequestInFlightRef = useRef<number | undefined>(
    undefined,
  );
  const loadedDeviceRef = useRef(false);
  const activeDeviceIdRef = useRef<string | undefined>(deviceId);
  const activeDeviceGenerationRef = useRef(0);

  useLayoutEffect(() => {
    activeDeviceIdRef.current = deviceId;
    activeDeviceGenerationRef.current += 1;

    return () => {
      if (activeDeviceIdRef.current === deviceId) {
        activeDeviceIdRef.current = undefined;
      }
    };
  }, [deviceId]);

  const loadDevice = useCallback(
    async (showLoading: boolean) => {
      const requestedDeviceId = deviceId || '';
      const requestedDeviceGeneration =
        activeDeviceGenerationRef.current;
      if (showLoading) {
        setDeviceResult({ status: 'Loading' });
        loadedDeviceRef.current = false;
      }
      setDeviceRefreshError(undefined);

      const result = await getDevice(requestedDeviceId);
      if (
        activeDeviceIdRef.current !== requestedDeviceId ||
        activeDeviceGenerationRef.current !==
          requestedDeviceGeneration
      ) {
        return;
      }

      if (result.status === 'OK') {
        loadedDeviceRef.current = true;
        setDeviceResult(result);
      } else if (showLoading || !loadedDeviceRef.current) {
        setDeviceResult(result);
      } else {
        setDeviceRefreshError(
          getResultErrorMessage(
            result,
            'Could not refresh device data.',
          ),
        );
      }
    },
    [deviceId],
  );

  const fetchTimeline = useCallback(
    async (mode: EventLoadMode) => {
      const requestedDeviceId = deviceId;
      const requestedDeviceGeneration =
        activeDeviceGenerationRef.current;
      if (
        !requestedDeviceId ||
        eventRequestInFlightRef.current === requestedDeviceGeneration
      ) {
        return;
      }

      eventRequestInFlightRef.current = requestedDeviceGeneration;
      setEventRefreshError(undefined);

      if (mode === 'initial') {
        setDeviceTimeline({ status: 'Loading' });
        setOlderTimelineWindowEmpty(false);
        setEventsUpdatedAt(undefined);
      } else if (mode === 'newer') {
        setEventsRefreshing(true);
      } else {
        setLoadingOlderEvents(true);
      }

      const newestTimelineItem = newestTimelineItemRef.current;
      let startTime: string | undefined;
      let endTime: string | undefined;

      if (mode === 'newer') {
        endTime = new Date().toISOString();
        const oldestPendingCommandTimestamp =
          getOldestPendingCommandTimestamp(timelineRef.current);
        const refreshAnchor =
          oldestPendingCommandTimestamp ??
          newestTimelineItem?.timestamp;
        startTime = refreshAnchor
          ? shiftTimestamp(refreshAnchor, -EVENT_OVERLAP_MS)
          : shiftTimestamp(endTime, -EVENT_HISTORY_WINDOW_MS);
      } else if (mode === 'initial') {
        endTime = new Date().toISOString();
        startTime = shiftTimestamp(endTime, -EVENT_HISTORY_WINDOW_MS);
      } else if (mode === 'older') {
        const historyBoundary = oldestHistoryBoundaryRef.current;

        if (historyBoundary) {
          startTime = shiftTimestamp(
            historyBoundary,
            -EVENT_HISTORY_WINDOW_MS,
          );
          endTime = shiftTimestamp(historyBoundary, -1);
        }
      }

      try {
        const result = await getDeviceTimeline(
          requestedDeviceId,
          startTime,
          endTime,
        );

        if (
          activeDeviceIdRef.current !== requestedDeviceId ||
          activeDeviceGenerationRef.current !==
            requestedDeviceGeneration
        ) {
          return;
        }

        if (result.status === 'OK') {
          const existingEvents =
            mode === 'initial' ? [] : timelineRef.current;
          const mergedTimeline = mergeDeviceTimeline(
            existingEvents,
            result.data.items,
          );
          const commandHistoryIsAvailable =
            !result.data.commandHistoryError;

          timelineRef.current = mergedTimeline;
          if (commandHistoryIsAvailable) {
            newestTimelineItemRef.current = mergedTimeline[0];
            if (mode === 'initial' && startTime) {
              oldestHistoryBoundaryRef.current = startTime;
            }
            if (mode === 'older' && startTime) {
              oldestHistoryBoundaryRef.current = startTime;
              setOlderTimelineWindowEmpty(
                result.data.items.length === 0,
              );
            }
          }
          setDeviceTimeline({
            status: 'OK',
            data: {
              items: mergedTimeline,
              commandHistoryError: result.data.commandHistoryError,
            },
          });
          setEventsUpdatedAt(new Date());
        } else if (mode === 'initial') {
          timelineRef.current = [];
          newestTimelineItemRef.current = undefined;
          oldestHistoryBoundaryRef.current = undefined;
          setDeviceTimeline(result);
        } else {
          setEventRefreshError(
            getResultErrorMessage(
              result,
              'Could not refresh events.',
            ),
          );
        }
      } finally {
        if (
          eventRequestInFlightRef.current ===
          requestedDeviceGeneration
        ) {
          eventRequestInFlightRef.current = undefined;
        }
        if (
          activeDeviceIdRef.current === requestedDeviceId &&
          activeDeviceGenerationRef.current ===
            requestedDeviceGeneration
        ) {
          setEventsRefreshing(false);
          setLoadingOlderEvents(false);
        }
      }
    },
    [deviceId],
  );

  useEffect(() => {
    timelineRef.current = [];
    newestTimelineItemRef.current = undefined;
    oldestHistoryBoundaryRef.current = undefined;

    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;

      void loadDevice(true);
      void fetchTimeline('initial');
    });

    return () => {
      cancelled = true;
    };
  }, [fetchTimeline, loadDevice]);

  useEffect(() => {
    const pollEvents = () => {
      if (document.visibilityState === 'visible') {
        void fetchTimeline('newer');
      }
    };

    const interval = window.setInterval(
      pollEvents,
      EVENT_POLL_INTERVAL_MS,
    );
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pollEvents();
      }
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange,
    );
    return () => {
      window.clearInterval(interval);
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange,
      );
    };
  }, [fetchTimeline]);

  const refreshAfterSubmission = useCallback(() => {
    void loadDevice(false);
    void fetchTimeline('newer');
  }, [fetchTimeline, loadDevice]);

  return (
    <div className="p-3.5">
      <Section>
        <DeviceBasicInformation deviceResult={deviceResult} />
        {deviceRefreshError && (
          <div
            className="flex items-center gap-3 text-sm text-red-600"
            role="alert"
          >
            <span>
              Could not refresh device data. {deviceRefreshError}
            </span>
            <button
              type="button"
              onClick={() => void loadDevice(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              Retry device
            </button>
          </div>
        )}
      </Section>
      <Section>
        <div className="w-full">
          <div className="flex items-center justify-between pb-3">
            <div className="text-xl">Events and commands</div>
            <button
              type="button"
              onClick={() => void fetchTimeline('newer')}
              disabled={eventsRefreshing}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
            >
              {eventsRefreshing
                ? 'Refreshing...'
                : 'Refresh timeline'}
            </button>
          </div>

          {deviceTimeline.status === 'Loading' && (
            <div className="text-sm font-light">
              Loading events and commands...
            </div>
          )}

          {deviceTimeline.status === 'OK' &&
            deviceTimeline.data.items.length === 0 && (
              <div className="text-sm font-light">
                No events or commands found for this device.
              </div>
            )}

          {deviceTimeline.status === 'OK' &&
            deviceTimeline.data.items.map((item) => {
              const time = format(
                new Date(item.timestamp),
                'dd-MM-yyyy HH:mm:ss xxx',
              );
              return (
                <div
                  key={getDeviceTimelineItemKey(item)}
                  className="flex flex-row text-sm font-mono"
                >
                  <div
                    className="pr-5"
                    title={item.timestamp}
                    aria-label={`Timeline item time ${time}`}
                  >
                    {time}
                  </div>
                  {item.type === 'command' ? (
                    <CommandTimelineItem item={item} />
                  ) : (
                    <div>{item.description}</div>
                  )}
                </div>
              );
            })}

          {deviceTimeline.status === 'OK' &&
            deviceTimeline.data.commandHistoryError && (
              <div
                className="pt-3 text-sm text-amber-700"
                role="alert"
              >
                {deviceTimeline.data.commandHistoryError}
              </div>
            )}

          {deviceTimeline.status !== 'OK' &&
            deviceTimeline.status !== 'Loading' && (
              <div
                className="flex items-center gap-3 text-sm text-red-600"
                role="alert"
              >
                <span>
                  {getResultErrorMessage(
                    deviceTimeline,
                    'Could not load events and commands.',
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => void fetchTimeline('initial')}
                  disabled={eventsRefreshing}
                  className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  Retry timeline
                </button>
              </div>
            )}

          {eventRefreshError && (
            <div
              className="flex items-center gap-3 text-sm text-red-600"
              role="alert"
            >
              <span>{eventRefreshError}</span>
              <button
                type="button"
                onClick={() => void fetchTimeline('newer')}
                disabled={eventsRefreshing}
                className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                Retry timeline
              </button>
            </div>
          )}

          {eventsUpdatedAt && (
            <div className="text-xs font-light pt-3">
              Updated {format(eventsUpdatedAt, 'dd-MM-yyyy HH:mm:ss')}
            </div>
          )}

          {deviceTimeline.status === 'OK' && (
            <div className="pt-3">
              {olderTimelineWindowEmpty && (
                <div className="text-xs font-light">
                  No items found in the previous time window. You can
                  continue loading older history.
                </div>
              )}
              <button
                type="button"
                onClick={() => void fetchTimeline('older')}
                disabled={loadingOlderEvents}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                {loadingOlderEvents
                  ? 'Loading older timeline items...'
                  : 'Load older timeline items'}
              </button>
            </div>
          )}
        </div>
      </Section>
      <Section>
        <div>
          {deviceResult.status === 'OK' && (
            <div>
              <SendUpgradeFirmware
                device={deviceResult.data}
                onSubmitted={refreshAfterSubmission}
              />
              <SendSetFirmware
                device={deviceResult.data}
                desiredFirmwareVersion={
                  deviceResult.data.desiredFirmwareVersion ||
                  deviceResult.data.firmwareVersion
                }
                onSubmitted={refreshAfterSubmission}
              />
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

const CommandTimelineItem = ({
  item,
}: {
  item: IDeviceTimelineItem;
}) => {
  const commandDescription =
    item.description || `Command ID ${item.commandId ?? 'unknown'}`;
  const origin = item.originApplication || 'unknown source';
  const delivery = item.deliveredAt
    ? `delivered at ${format(
        new Date(item.deliveredAt),
        'dd-MM-yyyy HH:mm:ss',
      )}`
    : 'delivery pending';
  const outcome =
    item.wasAccepted === true
      ? 'device accepted'
      : item.wasAccepted === false
        ? 'device rejected'
        : 'device outcome pending';
  const result = [
    item.resultCode === null || item.resultCode === undefined
      ? undefined
      : `result code ${item.resultCode}`,
    item.comment || undefined,
  ]
    .filter(Boolean)
    .join(': ');

  return (
    <div>
      <span className="mr-2 rounded bg-blue-100 px-1 text-xs text-blue-800">
        Commandeur command
      </span>
      <span>
        {commandDescription} from {origin}; {delivery}; {outcome}
        {result ? `; ${result}` : ''}
      </span>
    </div>
  );
};

interface IProps {
  device: IDevice;
  onSubmitted?: () => void;
}

const SendUpgradeFirmware = ({ device, onSubmitted }: IProps) => {
  const [command, setCommand] = useState<Result<string>>({
    status: '',
  });

  const upgradeDisabledReason = getUpgradeDisabledReason(device);

  const sendUpgradeFirmwareCommand = async () => {
    if (command.status === 'Loading' || upgradeDisabledReason) {
      return;
    }

    setCommand({ status: 'Loading' });
    const result = await sendUpgradeToLatestCommand(
      device.productCode,
      [device.serialNumber],
      device.variant,
    );
    setCommand(result);
    if (
      result.status === 'OK' &&
      !(
        typeof result.data === 'string' &&
        result.data.startsWith('No firmware command sent.')
      )
    ) {
      onSubmitted?.();
    }
  };

  return (
    <div className="bg-gray-50 w-50 rounded overflow-hidden mt-4">
      <div className="px-6 py-4">
        <div className="font-bold text-xl mb-2">
          <div className="mb-5">Upgrade firmware </div>
          <Button
            onClick={sendUpgradeFirmwareCommand}
            text={'Send command'}
            disabled={[
              {
                disabled: command.status === 'Loading',
                reason: 'A request is already being submitted.',
              },
              {
                disabled: upgradeDisabledReason !== undefined,
                reason:
                  upgradeDisabledReason ??
                  'This device is not ready for a firmware upgrade.',
              },
            ]}
          />
          <DeviceCommandStatus result={command} kind="upgrade" />
          <p className="mt-3 text-xs font-thin text-gray-600">
            Attempts to send the pending firmware upgrade. The device
            must have a newer desired firmware and be eligible for an
            upgrade.
          </p>
        </div>
      </div>
    </div>
  );
};

function getUpgradeDisabledReason(
  device: IDevice,
): string | undefined {
  const desiredFirmware = device.desiredFirmwareVersion;
  const currentFirmware = device.firmwareVersion;

  if (device.hasIssue) {
    return 'This device has a reported issue and is not eligible for an upgrade.';
  }

  if (desiredFirmware === null) {
    return 'No pending firmware update is set for this device.';
  }

  if (desiredFirmware === currentFirmware) {
    return `No upgrade is pending. The device is already on firmware ${currentFirmware}.`;
  }

  if (desiredFirmware < currentFirmware) {
    return `Cannot upgrade from firmware ${currentFirmware} to older firmware ${desiredFirmware}.`;
  }

  return undefined;
}

function shiftTimestamp(timestamp: string, milliseconds: number) {
  const parsedTimestamp = new Date(timestamp).getTime();
  if (Number.isNaN(parsedTimestamp)) return timestamp;

  return new Date(parsedTimestamp + milliseconds).toISOString();
}

function getResultErrorMessage(
  result: Result<unknown>,
  fallback: string,
) {
  if (
    result.status === 'Error' ||
    result.status === 'Not Found' ||
    result.status === 'Network issue' ||
    result.status === 'Forbidden'
  ) {
    return `${result.status}: ${result.errorMessages.join(', ')}`;
  }

  return fallback;
}
